import { useEffect, useRef, useState, useMemo } from 'react';
import { ProposalProvider, FeedbackProvider, useProposal } from './context';
import { getProposalIdFromUrl } from './utils/contentLoader';
import { Header, Footer, SectionDivider } from './components/layout';
import { PasswordProtection, ExpiredProposal } from './components/auth';
import { FeedbackModal, FeedbackSidebar } from './components/feedback';
import {
  HeroSection,
  EquipmentSection,
  AddonsSection,
  ServicesSection,
  TradeInSection,
  FinancingSection,
  PricingSection,
  AboutSection,
  NextStepsSection,
  // Service Proposal Sections
  EquipmentSpotlightSection,
  TaskSection,
  EstimateSummarySection,
} from './components/sections';
import './index.css';

export type TaskDecision = { accepted?: boolean; declined?: boolean; reason?: string };

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-surface-dark flex items-center justify-center">
      <div className="text-center">
        {/* Animated Logo */}
        <div className="relative w-20 h-20 mx-auto mb-6">
          <div className="absolute inset-0 bg-hul-red transform rotate-45 animate-pulse" />
          <div className="absolute inset-1 bg-surface-dark transform rotate-45" />
          <div className="absolute inset-2 bg-hul-red transform rotate-45 flex items-center justify-center">
            <span className="font-display text-white text-lg transform -rotate-45">
              HUL
            </span>
          </div>
        </div>
        <div className="font-display text-2xl text-white tracking-wider mb-2">
          HERC-U-LIFT
        </div>
        <div className="text-gray-400 text-sm">Loading your proposal...</div>
        {/* Loading Bar */}
        <div className="mt-6 w-48 h-1 bg-white/10 rounded-full overflow-hidden mx-auto">
          <div className="h-full bg-gradient-to-r from-hul-red to-hul-orange animate-pulse" style={{ width: '60%' }} />
        </div>
      </div>
    </div>
  );
}

function ErrorScreen({ message }: { message: string }) {
  return (
    <div className="min-h-screen bg-surface-dark flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="font-display text-2xl text-hul-navy tracking-wide mb-4">
          UNABLE TO LOAD PROPOSAL
        </h1>
        <p className="text-gray-600 mb-6">{message}</p>
        <a
          href="/"
          className="inline-block px-6 py-3 bg-hul-navy text-white font-heading uppercase tracking-wider rounded-lg hover:bg-hul-navy-dark transition-colors"
        >
          Go Home
        </a>
      </div>
    </div>
  );
}

function ProposalContent() {
  const { state } = useProposal();
  const pdfTriggered = useRef(false);

  // State for task decisions (for service proposals)
  const [taskDecisions, setTaskDecisions] = useState<Record<string, TaskDecision>>({});

  // Extract data for dependency tracking
  const proposalData = state.status === 'loaded' ? state.data : null;
  const tasks = proposalData?.tasks;
  const proposalType = proposalData?.proposalType;

  // Initialize task decisions from proposal data when loaded
  useEffect(() => {
    if (proposalType === 'service' && tasks) {
      const initial: Record<string, TaskDecision> = {};
      tasks.forEach(task => {
        initial[task.lineId] = {
          accepted: task.accepted,
          declined: task.declined,
          reason: task.declinedReason,
        };
      });
      setTaskDecisions(initial);
    }
  }, [proposalType, tasks]);

  // Calculate accepted total for service proposals
  const acceptedTotal = useMemo(() => {
    if (proposalType !== 'service' || !tasks) return 0;
    return tasks.reduce((sum, task) => {
      const decision = taskDecisions[task.lineId];
      if (decision?.accepted) {
        return sum + (task.price || 0);
      }
      return sum;
    }, 0);
  }, [proposalType, tasks, taskDecisions]);

  // Auto-download PDF when ?download=pdf parameter is present
  useEffect(() => {
    if (state.status !== 'loaded' || pdfTriggered.current) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('download') === 'pdf') {
      pdfTriggered.current = true;

      // Function to scroll through page and trigger all whileInView animations
      const triggerAllAnimations = async () => {
        const scrollHeight = document.documentElement.scrollHeight;
        const viewportHeight = window.innerHeight;
        const scrollStep = viewportHeight * 0.8; // Scroll 80% of viewport at a time

        // Scroll down through the page to trigger all whileInView animations
        for (let scrollPos = 0; scrollPos <= scrollHeight; scrollPos += scrollStep) {
          window.scrollTo({ top: scrollPos, behavior: 'instant' });
          await new Promise(resolve => setTimeout(resolve, 100)); // Small delay for animations to register
        }

        // Scroll to very bottom to ensure all content is triggered
        window.scrollTo({ top: scrollHeight, behavior: 'instant' });
        await new Promise(resolve => setTimeout(resolve, 300));

        // Scroll back to top
        window.scrollTo({ top: 0, behavior: 'instant' });
        await new Promise(resolve => setTimeout(resolve, 200));
      };

      setTimeout(async () => {
        try {
          // Trigger all animations by scrolling through the page
          await triggerAllAnimations();

          // Lazy load PDF generator only when needed
          const { generateProposalPDF } = await import('./utils/pdfGenerator');
          await generateProposalPDF({
            proposalId: state.data.proposal.id,
            customerCompany: state.data.customer.company,
          });
          // Remove the download parameter from URL
          window.history.replaceState({}, '', window.location.pathname);
        } catch (error) {
          console.error('Auto PDF generation failed:', error);
        }
      }, 2000);
    }
  }, [state]);

  switch (state.status) {
    case 'loading':
      return <LoadingScreen />;

    case 'error':
      return <ErrorScreen message={state.error} />;

    case 'expired':
      return <ExpiredProposal validUntil={state.validUntil} />;

    case 'protected':
      return <PasswordProtection />;

    case 'loaded':
      const isServiceProposal = state.data.proposalType === 'service';

      return (
        <FeedbackProvider>
          <div className="min-h-screen">
            <Header />

            <main>
              <HeroSection />
              <SectionDivider variant="accent" />

              {isServiceProposal ? (
                // Service Proposal Layout
                <>
                  <EquipmentSpotlightSection />
                  <SectionDivider />
                  <TaskSection
                    taskDecisions={taskDecisions}
                    onTaskDecisionChange={setTaskDecisions}
                  />
                  <SectionDivider />
                  <EstimateSummarySection
                    taskDecisions={taskDecisions}
                  />
                  <SectionDivider />
                  <ServicesSection />
                  <SectionDivider />
                  <TradeInSection />
                  <SectionDivider />
                  <FinancingSection />
                </>
              ) : (
                // Equipment Proposal Layout (default)
                <>
                  <EquipmentSection />
                  <SectionDivider />
                  <AddonsSection />
                  <SectionDivider />
                  <ServicesSection />
                  <SectionDivider />
                  <TradeInSection />
                  <SectionDivider />
                  <FinancingSection />
                  <SectionDivider />
                  <PricingSection />
                </>
              )}

              <SectionDivider />
              <AboutSection />
              <SectionDivider />
              <NextStepsSection
                acceptedTotal={isServiceProposal ? acceptedTotal : undefined}
              />
            </main>

            <Footer />

            {/* Feedback Components */}
            <FeedbackModal />
            <FeedbackSidebar />
          </div>
        </FeedbackProvider>
      );
  }
}

function App() {
  // Get proposal ID from URL path (e.g., /25 or /proposal/25)
  const proposalId = getProposalIdFromUrl() ?? undefined;

  return (
    <ProposalProvider proposalId={proposalId}>
      <ProposalContent />
    </ProposalProvider>
  );
}

export default App;
